"use client";
import { FunctionalIcon } from '@/app/Components/FunctionalIcon';
import Image from 'next/image';
import { friendOverlayColor } from '@/app/lib/friendOverlayColors';
import React, { useState, useEffect, useCallback } from 'react';
import { Preferences, ScheduledCourse } from '../types/course';
import { openSectionsService } from '../services/api';
import ContextMenu from './ContextMenu';
import CourseDetailsPopup from './CourseDetailsPopup';
import { useCourseContextMenu } from '@/app/hooks/useCourseContextMenu';
import { isSemesterId } from '@/app/lib/semesters';
import Toast from './Toast';
import { CAMPUS_COLOR_KEY_ENTRIES, CAMPUS_COLORS } from '../types/colors';
import { StarIcon } from '@heroicons/react/24/solid';
import { HomeIcon, ArrowPathIcon, MagnifyingGlassIcon, PlusIcon, CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { formatBaseIdDisplay, isGradCourse } from '../lib/utils';
import { AVAILABILITY_DAYS, AVAILABILITY_SLOT_COUNT, AVAILABILITY_START_HOUR, formatAvailabilityHour } from '../lib/availabilityGrid';
import teachersData from '@/data/teachers_data.json';
import type { NoScheduleReason } from '../types/interfaces';
import InstructorSIRSRating from './SIRS/InstructorSIRSRating';
import { useTheme } from '@/app/Components/ThemeProvider';
import ScheduleCoursePreview, { getScheduleCoursePreviewPosition } from '@/app/scheduling/components/ScheduleCoursePreview';
import { hasRecitationMeeting, isRecitationMeeting } from '@/app/lib/meetingTypes';

// Hour markings for the schedule grid
const HOURS = Array.from({ length: 16 }, (_, i) => i + 8); // 8 AM to 11 PM

// Days to display in the schedule
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

type TimeBlockSelection = { day: string; startMinute: number; endMinute: number };

export interface ScheduleViewProps {
  schedule: ScheduledCourse[];
  /** Current course inputs are distinct from an empty generated timetable. */
  selectedCourseCount?: number;
  onNextSchedule: () => void;
  onPrevSchedule: () => void;
  onHomeSchedule?: () => void; // New prop for going back to selected schedule
  currentScheduleIndex: number;
  totalSchedules: number;
  totalSchedulesGenerated?: number; // Total schedules generated (may be more than displayed)
  allSectionsByCourseId?: { [courseId: string]: ScheduledCourse[] };
  onSaveSchedule?: (scheduleIndex: number) => void; // New prop for saving schedules
  isSelectedSchedule?: boolean; // Whether this is the currently selected/loaded schedule
  onMakePrimary?: (scheduleIndex: number) => void; // New prop for making a schedule primary
  onGenerateRandomSchedule?: () => void; // New prop for generating random schedule
  onTimeBlockSelect?: (selection: TimeBlockSelection) => void;
  onFindClassesInTimeBlock?: (selection: TimeBlockSelection) => void;
  onMakeTimeBlockAvailable?: (selection: TimeBlockSelection) => void;
  showAvailabilityOverlay?: boolean;
  preferences?: Preferences | null;
  currentScheduleScore?: number; // Score of the current schedule
  onExportToGoogleCalendar?: (courses: ScheduledCourse[]) => Promise<{ success: boolean; message: string; eventCount: number }>; // New prop for Google Calendar export
  // Friends overlay: map course/section identifiers to friend avatar URLs
  friendPresenceBySection?: Record<string, { avatarUrl?: string; displayName?: string; userId: string }[]>;
  // Conflict detection props
  conflicts?: Array<{
    course1: ScheduledCourse;
    course2: ScheduledCourse;
    day: string;
    timeConflict: string;
  }>;
  onShowConflicts?: () => void;
  // Export mode - when true, only renders the schedule grid for image export
  exportMode?: boolean;
  // Mobile mode - when true, renders a mobile-optimized layout
  isMobile?: boolean;
  // Semester information
  semester?: string;
  // No schedule reasons - why schedules couldn't be generated
  noScheduleReasons?: NoScheduleReason[] | null;
  // Callback to open the no schedule modal
  onOpenNoScheduleModal?: () => void;
  // Callback to configure sections when a course is clicked
  onCourseConfigRequest?: (course: ScheduledCourse) => void;
  regenIndicatorVisible?: boolean;
  // Set of course IDs that came from course lists (for visual indication)
  coursesFromCourseLists?: Set<string>;
  // Friend schedules to overlay on the grid
  overlayFriendSchedules?: { friendName: string, color?: string, courses: ScheduledCourse[] }[];
  showOwnSchedule?: boolean;
  showDetailedCourseInformation?: boolean;
  showCreditLoadStatus?: boolean;
  headerActions?: React.ReactNode;
  workspaceTabs?: React.ReactNode;
  contentOverride?: React.ReactNode;
  courseChangeStates?: Record<string, 'added' | 'removed'>;
  /** Deterministic owner-free fixtures can supply availability without requesting live section state. */
  sectionAvailabilityOverride?: Readonly<Record<string, boolean>>;
  showSectionAvailability?: boolean;
}

const ScheduleView = React.forwardRef<HTMLDivElement, ScheduleViewProps>(({ schedule, selectedCourseCount, onNextSchedule, onPrevSchedule, onHomeSchedule, currentScheduleIndex, totalSchedules, totalSchedulesGenerated, allSectionsByCourseId, onSaveSchedule, isSelectedSchedule = false, onMakePrimary, onGenerateRandomSchedule, onTimeBlockSelect, onFindClassesInTimeBlock, onMakeTimeBlockAvailable, showAvailabilityOverlay = true, preferences, currentScheduleScore, onExportToGoogleCalendar, friendPresenceBySection, conflicts = [], onShowConflicts, exportMode, isMobile = false, semester, noScheduleReasons, onOpenNoScheduleModal, onCourseConfigRequest, regenIndicatorVisible = false, coursesFromCourseLists, overlayFriendSchedules, showOwnSchedule = true, showDetailedCourseInformation = true, showCreditLoadStatus = true, headerActions, workspaceTabs, contentOverride, courseChangeStates = {}, sectionAvailabilityOverride, showSectionAvailability = true }, ref) => {
  const { theme } = useTheme();
  // State to track window width for responsive design
  const [isSmallScreen, setIsSmallScreen] = React.useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const [dragSelection, setDragSelection] = useState<{
    day: string;
    anchorMinute: number;
    startMinute: number;
    endMinute: number;
  } | null>(null);
  const [selectedTimeBlock, setSelectedTimeBlock] = useState<{
    day: string;
    startMinute: number;
    endMinute: number;
  } | null>(null);

  // Popup state belongs to the displayed term that supplied its course.
  const popupSemester = isSemesterId(semester) ? semester : undefined;
  const popupTermToken = React.useMemo(() => ({ semester: popupSemester }), [popupSemester]);
  const committedPopupTerm = React.useRef<typeof popupTermToken | null>(null);
  React.useLayoutEffect(() => {
    committedPopupTerm.current = popupTermToken;
    return () => {
      if (committedPopupTerm.current === popupTermToken) committedPopupTerm.current = null;
    };
  }, [popupTermToken]);
  const [openedPopupTerm, setOpenedPopupTerm] = useState<typeof popupTermToken | null>(null);
  const [popupCourse, setPopupCourse] = useState<ScheduledCourse | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [popupAnchorElement, setPopupAnchorElement] = useState<HTMLElement | null>(null);
  const [hoverPreview, setHoverPreview] = useState<{
    course: ScheduledCourse;
    left: number;
    top: number;
  } | null>(null);

  // Context menu state


  // Toast notification state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });

  // Google Calendar export loading state
  const [isExporting, setIsExporting] = useState(false);

  const popupSurfaceClassName = theme === 'light'
    ? 'border border-slate-200/90 bg-white/96 text-slate-900 shadow-e3 backdrop-blur-xl'
    : 'border border-white/15 bg-slate-950/95 text-white shadow-e3 backdrop-blur-sm';
  const popupMutedClassName = theme === 'light' ? 'text-slate-500' : 'text-white/75';
  const popupSubtleClassName = theme === 'light' ? 'bg-slate-100 text-slate-700' : 'bg-white/10 text-white/85';
  const popupHoverClassName = theme === 'light' ? 'hover:bg-slate-100/95' : 'hover:bg-white/10';


  // Load open sections data
  useEffect(() => {
    if (sectionAvailabilityOverride || !showSectionAvailability) return;
    const loadOpenSections = async () => {
      try {
        await openSectionsService.refreshOpenSections();
        // The service caches the data, so we don't need to store it in state
      } catch (error) {
        console.error('Failed to load open sections:', error);
      }
    };

    loadOpenSections();
  }, [sectionAvailabilityOverride, showSectionAvailability]);

  // Helper function to check if a section is open
  const isSectionOpen = (sectionId: string): boolean => {
    return sectionAvailabilityOverride?.[sectionId] ?? openSectionsService.isOpen(sectionId);
  };

  const isCustomActivity = (course: ScheduledCourse): boolean => {
    return course.id.startsWith('custom:') || course.department === 'CUSTOM';
  };

  // Effect to handle window resize and check screen size
  React.useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };

    // Check on initial render
    checkScreenSize();

    // Add event listener for window resize
    window.addEventListener('resize', checkScreenSize);

    // Cleanup on unmount
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    if (exportMode || !totalSchedulesGenerated) return;

    const handleScheduleArrowKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const target = event.target;
      if (target instanceof Element && target.closest('input, textarea, select, [contenteditable="true"], [role="dialog"]')) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onPrevSchedule();
      } else if (event.key === 'ArrowRight' && (isSelectedSchedule || currentScheduleIndex < totalSchedules - 1)) {
        event.preventDefault();
        onNextSchedule();
      }
    };

    window.addEventListener('keydown', handleScheduleArrowKey);
    return () => window.removeEventListener('keydown', handleScheduleArrowKey);
  }, [currentScheduleIndex, exportMode, isSelectedSchedule, onNextSchedule, onPrevSchedule, totalSchedules, totalSchedulesGenerated]);

  // Helper to convert minute count to formatted time string
  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  const getSnappedMinuteFromPointer = (
    event: React.MouseEvent<HTMLElement>,
    element: HTMLElement
  ) => {
    const rect = element.getBoundingClientRect();
    const y = Math.min(Math.max(event.clientY - rect.top, 0), rect.height);
    const rawMinute = earliestHour * 60 + (y / rect.height) * (totalHours * 60);
    const snapped = Math.round(rawMinute / 15) * 15;
    return Math.min(Math.max(snapped, earliestHour * 60), (earliestHour + totalHours) * 60);
  };

  const updateDragSelection = (
    day: string,
    minute: number,
    anchorMinute = dragSelection?.anchorMinute ?? minute
  ) => {
    const startMinute = Math.min(anchorMinute, minute);
    const endMinute = Math.max(anchorMinute, minute + 15);
    setDragSelection({
      day,
      anchorMinute,
      startMinute,
      endMinute: Math.max(endMinute, startMinute + 15),
    });
  };

  const completeTimeBlockSelection = useCallback((selection: TimeBlockSelection) => {
    setSelectedTimeBlock(selection);
    setDragSelection(null);
  }, []);

  const handleTimeBlockMouseDown = (event: React.MouseEvent<HTMLDivElement>, day: string) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const minute = getSnappedMinuteFromPointer(event, event.currentTarget);
    updateDragSelection(day, minute, minute);
  };

  const handleTimeBlockMouseMove = (event: React.MouseEvent<HTMLDivElement>, day: string) => {
    if (!dragSelection || dragSelection.day !== day) return;
    const minute = getSnappedMinuteFromPointer(event, event.currentTarget);
    updateDragSelection(day, minute);
  };

  const handleTimeBlockMouseUp = (event: React.MouseEvent<HTMLDivElement>, day: string) => {
    if (!dragSelection || dragSelection.day !== day) return;
    const minute = getSnappedMinuteFromPointer(event, event.currentTarget);
    const startMinute = Math.min(dragSelection.anchorMinute, minute);
    const endMinute = Math.max(dragSelection.anchorMinute, minute + 15);
    completeTimeBlockSelection({
      day,
      startMinute,
      endMinute: Math.max(endMinute, startMinute + 15),
    });
  };

  useEffect(() => {
    if (!dragSelection) return;
    const handleMouseUp = () => {
      completeTimeBlockSelection({
        day: dragSelection.day,
        startMinute: dragSelection.startMinute,
        endMinute: dragSelection.endMinute,
      });
    };
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, [completeTimeBlockSelection, dragSelection]);

  const formatClassIndex = (index?: string | number | null): string => {
    if (index === undefined || index === null) {
      return 'N/A';
    }
    const normalized = String(index).trim();
    if (normalized.length === 0) {
      return 'N/A';
    }
    return /^\d+$/.test(normalized) ? normalized.padStart(5, '0') : normalized;
  };

  // Helper to check if a course is involved in conflicts
  const isCourseInConflict = (courseId: string): boolean => {
    return conflicts.some(conflict =>
      conflict.course1.id === courseId || conflict.course2.id === courseId
    );
  };

  // Use the new course context menu hook
  const {
    contextMenu,
    handleContextMenu,
    closeContextMenu,
    handleAddToSniper,
    handleViewInExplore,
    handleCopyCourseInfo,
    handleShareCourse
  } = useCourseContextMenu(semester);

  // Popup dispatch must not relabel a retained course after a term replacement.
  const handlePopupAddToSniper = useCallback((course: ScheduledCourse) => {
    if (openedPopupTerm !== popupTermToken || committedPopupTerm.current !== popupTermToken) return;
    handleAddToSniper(course);
  }, [openedPopupTerm, popupTermToken, handleAddToSniper]);
  const handlePopupViewInExplore = useCallback((course: ScheduledCourse) => {
    if (openedPopupTerm !== popupTermToken || committedPopupTerm.current !== popupTermToken) return;
    handleViewInExplore(course);
  }, [openedPopupTerm, popupTermToken, handleViewInExplore]);

  // Popup handlers
  const updatePopupPosition = useCallback((
    opts?: { anchor?: HTMLElement | null; fallback?: { x: number; y: number } }
  ) => {
    const anchor = opts?.anchor ?? popupAnchorElement;
    if (anchor) {
      const rect = anchor.getBoundingClientRect();
      const padding = 16;
      const clampedX = Math.min(
        window.innerWidth - padding,
        Math.max(padding, rect.left + rect.width / 2)
      );
      const clampedY = Math.max(padding, rect.top);
      setPopupPosition({ x: clampedX, y: clampedY });
      return;
    }
    if (opts?.fallback) {
      setPopupPosition(opts.fallback);
    }
  }, [popupAnchorElement]);

  const openCourseDetailsPopup = useCallback((
    anchorEl: HTMLElement | null,
    course: ScheduledCourse,
    fallbackPosition?: { x: number; y: number }
  ) => {
    if ((!anchorEl && !fallbackPosition) || committedPopupTerm.current !== popupTermToken) return;
    setOpenedPopupTerm(popupTermToken);
    setSelectedCourseId(course.id);
    setPopupAnchorElement(anchorEl ?? null);
    setPopupCourse(course);
    setIsPopupOpen(true);
    updatePopupPosition({ anchor: anchorEl, fallback: fallbackPosition });
  }, [updatePopupPosition, popupTermToken]);

  const handleCourseCardClick = (e: React.MouseEvent<HTMLElement>, course: ScheduledCourse) => {
    e.stopPropagation();
    if (onCourseConfigRequest) {
      setIsPopupOpen(false);
      setPopupCourse(null);
      setPopupAnchorElement(null);
      onCourseConfigRequest(course);
      return;
    }
    openCourseDetailsPopup(e.currentTarget, course, { x: e.clientX, y: e.clientY });
  };

  const handleCourseCardRightClick = (e: React.MouseEvent<HTMLElement>, course: ScheduledCourse) => {
    e.preventDefault();
    e.stopPropagation();
    openCourseDetailsPopup(e.currentTarget, course, { x: e.clientX, y: e.clientY });
  };

  const handleCourseCardKeyDown = (event: React.KeyboardEvent<HTMLElement>, course: ScheduledCourse) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    if (onCourseConfigRequest) {
      setIsPopupOpen(false);
      setPopupCourse(null);
      setPopupAnchorElement(null);
      onCourseConfigRequest(course);
      return;
    }
    openCourseDetailsPopup(event.currentTarget, course);
  };

  const openHoverPreview = (anchorEl: HTMLElement, course: ScheduledCourse) => {
    const rect = anchorEl.getBoundingClientRect();
    const position = getScheduleCoursePreviewPosition(
      rect,
      { width: window.innerWidth, height: window.innerHeight },
      'vertical',
    );
    setHoverPreview({
      course,
      ...position,
    });
  };

  const closeHoverPreview = () => {
    setHoverPreview(null);
  };

  // Section maps have both base-course and registration-index keys across
  // callers. Match the canonical course identity and deduplicate by section ID.
  const alternativeSections = React.useMemo(() => {
    if (!hoverPreview || isCustomActivity(hoverPreview.course) || !showOwnSchedule || !showSectionAvailability || isMobile) return [];
    const unique = new Map<string, ScheduledCourse>();
    for (const sections of Object.values(allSectionsByCourseId ?? {})) {
      for (const section of sections) {
        if (section.baseId === hoverPreview.course.baseId && section.id !== hoverPreview.course.id) {
          unique.set(section.id, section);
        }
      }
    }
    return Array.from(unique.values());
  }, [hoverPreview, allSectionsByCourseId, showOwnSchedule, showSectionAvailability, isMobile]);

  useEffect(() => {
    if (!hoverPreview) return;
    const closePreview = () => setHoverPreview(null);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closePreview();
    };
    window.addEventListener('resize', closePreview);
    window.addEventListener('scroll', closePreview, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('resize', closePreview);
      window.removeEventListener('scroll', closePreview, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [hoverPreview]);

  const closePopup = () => {
    setIsPopupOpen(false);
    setPopupCourse(null);
    setPopupAnchorElement(null);
  };
  useEffect(() => {
    if (!isPopupOpen) return;
    const handleScrollOrResize = () => updatePopupPosition();
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    handleScrollOrResize();
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isPopupOpen, updatePopupPosition]);

  useEffect(() => {
    if (schedule.length === 0) {
      setSelectedCourseId(null);
      return;
    }

    setSelectedCourseId((current) =>
      current && schedule.some((course) => course.id === current)
        ? current
        : null
    );
  }, [schedule]);

  // Professor rating functionality
  interface Professor {
    id: string;
    firstName: string;
    lastName: string;
    avgDifficulty: number;
    avgRating: number;
    department: string;
    numRatings: number;
    wouldTakeAgainPercent: number;
  }

  const typedTeachersData: Professor[] = teachersData as Professor[];

  // Function to find professor data by name
  const findProfessorData = (professorName: string): Professor | null => {
    if (!professorName || professorName === 'TBA') return null;

    // Clean up the professor name (remove commas, extra spaces)
    const cleanName = professorName.replace(/,/g, '').trim().toLowerCase();

    return typedTeachersData.find(prof => {
      const fullName = `${prof.firstName} ${prof.lastName}`.toLowerCase();
      const reverseName = `${prof.lastName} ${prof.firstName}`.toLowerCase();
      return fullName.includes(cleanName) || reverseName.includes(cleanName) || cleanName.includes(fullName);
    }) || null;
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4.0) return 'text-green-500';
    if (rating >= 3.0) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getDifficultyColor = (difficulty: number) => {
    if (difficulty >= 4.0) return 'text-red-500';
    if (difficulty >= 3.0) return 'text-yellow-500';
    return 'text-green-500';
  };

  // Get campus-based styling for courses
  const getCampusStyle = (campus: string): string => {
    // Normalize campus name
    const normalizedCampus = campus?.trim() || 'Unknown';

    // Check for online/remote indicators
    if (normalizedCampus.toLowerCase().includes('online') ||
      normalizedCampus.toLowerCase().includes('web')) {
      return CAMPUS_COLORS['Online'];
    }
    if (normalizedCampus.toLowerCase().includes('remote')) {
      return CAMPUS_COLORS['Remote'];
    }

    // Handle common campus variations
    if (normalizedCampus.toLowerCase().includes('c/d') ||
      normalizedCampus.toLowerCase().includes('cook') ||
      normalizedCampus.toLowerCase().includes('douglass')) {
      return CAMPUS_COLORS['Cook/Douglass'];
    }

    if (normalizedCampus.toLowerCase().includes('busch')) {
      return CAMPUS_COLORS['Busch'];
    }

    if (normalizedCampus.toLowerCase().includes('college avenue') ||
      normalizedCampus.toLowerCase().includes('college ave')) {
      return CAMPUS_COLORS['College Ave'];
    }

    if (normalizedCampus.toLowerCase().includes('livingston')) {
      return CAMPUS_COLORS['Livingston'];
    }

    if (normalizedCampus.toLowerCase().includes('downtown') ||
      normalizedCampus.toLowerCase().includes('new brunswick')) {
      return CAMPUS_COLORS['Downtown New Brunswick'];
    }

    if (normalizedCampus.toLowerCase().includes('camden')) {
      return CAMPUS_COLORS['Camden'];
    }

    if (normalizedCampus.toLowerCase().includes('newark')) {
      return CAMPUS_COLORS['Newark'];
    }

    // Handle NB as default New Brunswick
    if (normalizedCampus === 'NB' || normalizedCampus.toLowerCase() === 'nb') {
      return CAMPUS_COLORS['NB'];
    }

    // Return campus-specific style or default
    return CAMPUS_COLORS[normalizedCampus as keyof typeof CAMPUS_COLORS] || CAMPUS_COLORS['NB'];
  };

  const getPrimaryCampusValue = (courseLike: { campus: string; campuses?: string[] }) => {
    if (courseLike.campuses && courseLike.campuses.length > 0) {
      const unique = Array.from(new Set(courseLike.campuses.map((c) => c?.trim()).filter(Boolean)));
      if (unique.length > 0) {
        return unique[0];
      }
    }
    return courseLike.campus;
  };

  const formatCampusDisplay = (courseLike: { campus: string; campuses?: string[] }) => {
    if (courseLike.campuses && courseLike.campuses.length > 0) {
      const unique = Array.from(new Set(courseLike.campuses.map((c) => c?.trim()).filter(Boolean)));
      if (unique.length > 0) {
        return unique.join(', ');
      }
    }
    return courseLike.campus;
  };

  const getMeetingCampusValue = (
    courseLike: { campus: string; campuses?: string[] },
    meetingTime?: { campus?: string | null }
  ): string => {
    const meetingCampus = meetingTime?.campus?.trim();
    if (meetingCampus && meetingCampus.length > 0) {
      return meetingCampus;
    }
    return getPrimaryCampusValue(courseLike);
  };

  // Calculate display values for the schedule
  const earliestHour = 8; // 8 AM
  const latestHour = 23; // 11 PM
  const totalHours = latestHour - earliestHour + 1;
  const hourHeight = 60; // Reduced height (~75% of original) for more compact view

  const renderAvailabilityTint = (day: string) => {
    const dayIndex = AVAILABILITY_DAYS.indexOf(day as typeof AVAILABILITY_DAYS[number]);
    const dayAvailability = dayIndex >= 0 ? preferences?.availability?.[dayIndex] : null;
    const slots = Array.isArray(dayAvailability)
      ? dayAvailability
      : Array(AVAILABILITY_SLOT_COUNT).fill(true);

    return Array.from({ length: AVAILABILITY_SLOT_COUNT }).map((_, slotIndex) => {
      const slotHour = AVAILABILITY_START_HOUR + slotIndex;
      const topPosition = (slotHour - earliestHour) * hourHeight;
      if (topPosition < 0 || topPosition >= totalHours * hourHeight) return null;

      const isFree = slots[slotIndex] !== false;
      // Free hours stay plain; only hours marked busy get a quiet hatch so class blocks keep the colour.
      if (isFree) return null;

      return (
        <div
          key={`${day}-availability-${slotIndex}`}
          data-availability-slot="true"
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0"
          style={{
            backgroundImage: 'repeating-linear-gradient(135deg, color-mix(in srgb, var(--ss-text-muted) 14%, transparent) 0 2px, transparent 2px 9px)',
            top: `${topPosition}px`,
            height: `${hourHeight}px`,
            zIndex: 1,
          }}
          title={`${day} ${formatAvailabilityHour(slotHour)} ${isFree ? 'free' : 'busy'}`}
        />
      );
    });
  };

  // If in export mode, only render the schedule grid
  if (exportMode) {
    // Calculate the latest time to determine grid width
    const latestTime = Math.max(...schedule.flatMap(course =>
      course.meetingTimes?.map(mt => mt.endMinute) || []
    ));
    const latestHour = Math.ceil(latestTime / 60);
    const maxDisplayHour = Math.min(latestHour + 1, 22); // Cap at 10 PM, but allow up to latest + 1 hour
    const totalDisplayHours = maxDisplayHour - earliestHour;

    return (
      <div ref={ref} className="bg-slate-100 dark:bg-slate-800 rounded-card shadow-e3 p-4 overflow-hidden" style={{ minWidth: '900px' }}>
        {/* Schedule Grid Container */}
        <div className="bg-slate-200 dark:bg-slate-900 rounded-control p-2">
          {/* Schedule header - days of the week */}
          <div className="grid grid-cols-6 gap-2 mb-2">
            <div className="text-center">
              <div className="text-slate-600 dark:text-slate-400 text-xs font-medium h-10 flex items-center justify-center gap-2">
                <span>Time</span>
                {typeof currentScheduleScore === 'number' && (
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-semibold border
                      ${currentScheduleScore >= 80
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700'
                        : currentScheduleScore >= 60
                          ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700'}`}
                  >
                    {currentScheduleScore}%
                  </span>
                )}
              </div>
            </div>
            {DAYS.map(day => (
              <div key={day} className="text-center">
                <div className="bg-slate-300 dark:bg-slate-700 text-slate-900 dark:text-white font-bold py-2 rounded-control">
                  <div className="text-xs" style={{ transform: 'translateY(-50%)' }}>{day}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Schedule grid */}
          <div className="relative grid grid-cols-6 gap-2" style={{ height: `${totalDisplayHours * hourHeight}px` }}>
            {/* Time markers column */}
            <div className="relative">
              {HOURS.filter(hour => hour <= maxDisplayHour).map((hour) => (
                <div
                  key={hour}
                  className="absolute w-full text-[10px] text-slate-600 dark:text-slate-400 font-medium flex justify-center items-center border-t border-slate-300 dark:border-slate-700" // Note: items-center is removed
                  style={{
                    top: `${(hour - earliestHour) * hourHeight}px`,
                    // The height property is removed
                  }}
                >
                  <span
                    className="bg-slate-600 dark:bg-slate-400 px-1 py-0.5 rounded inline-block"
                    style={{
                      transform: 'translateY(-50%)',
                      height: `0px` // Add a height to the container
                    }}
                  >
                    {formatTime(hour * 60)}
                  </span>
                </div>
              ))}
            </div>

            {/* Days columns */}
            {DAYS.map(day => (
              <div key={day} className="relative bg-white dark:bg-slate-800 rounded-control border border-slate-300 dark:border-slate-700">
                {/* Background hour lines */}
                {HOURS.filter(hour => hour <= maxDisplayHour).map((hour) => (
                  <div
                    key={hour}
                    className="absolute w-full border-t border-slate-200 dark:border-slate-700/50"
                    style={{
                      top: `${(hour - earliestHour) * hourHeight}px`,
                      height: '1px'
                    }}
                  />
                ))}

                {/* Export always includes the saved schedule. */}
                {schedule.filter(course =>
                  course.meetingTimes?.some(mt => mt.day === day)
                ).flatMap((course, courseIndex) => {
                  // Get all meeting times for this course on this day
                  const meetingTimes = course.meetingTimes.filter(mt => mt.day === day);

                  return meetingTimes.map((meetingTime, meetingIndex) => {
                    const startMinute = meetingTime.startMinute;
                    const endMinute = meetingTime.endMinute;

                    const startHour = startMinute / 60;
                    const duration = (endMinute - startMinute) / 60; // in hours

                    const topPosition = (startHour - earliestHour) * hourHeight;
                    const height = duration * hourHeight; // Use actual duration-based height for export mode
                    const showLocation = height >= 90; // Increased threshold for export mode
                    const showInstructor = height >= 110; // Increased threshold for export mode
                    const hasMultipleCampuses = (course.campuses || []).length > 1;
                    const showCampus = height >= 130 || (hasMultipleCampuses && height >= 90); // Increased threshold for export mode while avoiding overflow
                    const meetingCampusValue = getMeetingCampusValue(course, meetingTime);
                    const campusDisplay = meetingTime.campus?.trim()?.length
                      ? meetingTime.campus
                      : formatCampusDisplay(course);
                    const isGrad = isGradCourse(course.baseId || course.id, course.class_number);
                    // Check if course is from a course list, including normalized formats
                    const isFromCourseList = coursesFromCourseLists ? (
                      coursesFromCourseLists.has(course.id) ||
                      coursesFromCourseLists.has(course.baseId) ||
                      (course.id.includes(':') ? coursesFromCourseLists.has(course.id.replace(/\D/g, '')) : false) ||
                      (course.baseId.includes(':') ? coursesFromCourseLists.has(course.baseId.replace(/\D/g, '')) : false)
                    ) : false;

                    // Determine border and background style
                    // For course lists: red border with campus background (extract bg classes from campus style)
                    // For grad courses: purple border and background
                    // Otherwise: campus border and background
                    let styleClasses = '';
                    if (isFromCourseList) {
                      const campusStyle = getCampusStyle(meetingCampusValue);
                      // Extract background gradient classes (everything except border classes)
                      const bgClasses = campusStyle.split(' ').filter(cls => !cls.startsWith('border')).join(' ');
                      styleClasses = `border-l-red-600 ${bgClasses}`;
                    } else if (isGrad) {
                      styleClasses = 'border-l-purple-500 bg-gradient-to-r from-purple-600 to-purple-500';
                    } else {
                      styleClasses = getCampusStyle(meetingCampusValue);
                    }

                    return (
                      <div
                        key={`${course.id}-${courseIndex}-${meetingIndex}`}
                        className={`absolute left-1 right-1 p-2 rounded-control text-white shadow-e2 border-l-4 ${styleClasses}`}
                        style={{
                          top: `${topPosition + 2}px`,
                          height: `${height - 4}px`,
                          overflow: 'hidden',
                          zIndex: 20,
                        }}
                      >
                        <div className="h-full flex flex-col justify-between">
                          <div>
                            <div className="text-[10px] font-bold leading-tight mb-1 break-words">
                              {isRecitationMeeting(meetingTime) && <abbr title="Recitation" className="mr-1 rounded bg-slate-600 px-1 py-0.5 text-white no-underline">R</abbr>}{course.name}{isGrad && <span className="text-purple-200"> (GRAD)</span>}
                            </div>
                            {isFromCourseList && (
                              <div className="text-[8px] opacity-90 leading-tight mb-0.5 break-words text-red-200">
                                From Course List
                              </div>
                            )}
                            <div className="text-[9px] opacity-90 leading-tight mb-1 break-words">
                              {formatBaseIdDisplay(course.baseId)} • {course.section}
                            </div>
                          </div>

                          {(showLocation || showInstructor || showCampus) && (
                            <div className="text-[8px] opacity-80 leading-tight">
                              {showLocation && (
                                <div className="break-words mb-0.5">
                                  <FunctionalIcon name="location" /> {meetingTime.location || course.location || 'TBA'}
                                </div>
                              )}
                              {showInstructor && course.instructors && course.instructors.length > 0 && course.instructors[0] !== 'TBA' && (
                                <div className="break-words">
                                  <FunctionalIcon name="instructor" /> {typeof course.instructors[0] === 'string' ? course.instructors[0] : course.instructors[0].name}
                                </div>
                              )}
                              {showCampus && (
                                <div className="text-[8px] opacity-60 mt-0.5 break-words">
                                  <FunctionalIcon name="campus" /> {campusDisplay}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Mobile layout - vertical list of courses by day
  if (isMobile) {
    return (
      <div ref={ref} className="ss-scheduling-weekly min-h-full w-full overflow-hidden p-3">
        {/* Mobile Header */}
        <div className="flex flex-col items-center justify-between mb-4">
          <div className="mb-3 flex w-full flex-col gap-2">
            <div className="flex items-center justify-center gap-2">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Weekly Schedule</h2>
              {!isSelectedSchedule && onHomeSchedule ? (
                <button
                  onClick={onHomeSchedule}
                  className="flex min-h-12 min-w-12 items-center justify-center rounded-control bg-blue-200 text-blue-900 transition-colors hover:bg-blue-300 dark:bg-blue-700 dark:text-white dark:hover:bg-blue-600"
                  title="Return to selected schedule"
                  aria-label="Return to selected schedule"
                >
                  <HomeIcon className="h-4 w-4" />
                </button>
              ) : null}
            </div>
            {workspaceTabs}
          </div>
          {headerActions ? <div className="mb-3">{headerActions}</div> : null}
          {schedule.length === 0 && (selectedCourseCount ?? 0) === 0 && <p className="text-xs text-[var(--ss-text-muted)]">Add classes to see a schedule</p>}

          {/* Schedule Navigation */}
          {totalSchedulesGenerated && totalSchedulesGenerated > 0 && (
            <div className="mb-3 flex flex-wrap items-center justify-center gap-2">
              <button
                onClick={onPrevSchedule}
                className="flex min-h-12 min-w-12 items-center justify-center rounded-control bg-slate-200 text-slate-900 transition-colors hover:bg-slate-300 disabled:opacity-50 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600"
                disabled={(totalSchedulesGenerated ?? 0) === 0}
                aria-label="Previous schedule"
                title="Previous schedule (Left arrow)"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <span className="flex flex-col text-xs leading-tight text-slate-600 dark:text-slate-400">
                <span>{currentScheduleIndex === -1 ? 'Selected Schedule' : `Schedule ${currentScheduleIndex + 1} of ${totalSchedules}`}</span>
                {totalSchedulesGenerated !== totalSchedules && currentScheduleIndex !== -1 ? <span className="mt-0.5 text-[10px]">{totalSchedulesGenerated} generated</span> : null}
              </span>
              <button
                onClick={onNextSchedule}
                className="flex min-h-12 min-w-12 items-center justify-center rounded-control bg-slate-200 text-slate-900 transition-colors hover:bg-slate-300 disabled:opacity-50 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600"
                disabled={(totalSchedulesGenerated ?? 0) === 0 || (!isSelectedSchedule && currentScheduleIndex === totalSchedules - 1)}
                aria-label="Next schedule"
                title="Next schedule (Right arrow)"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
              {onGenerateRandomSchedule && totalSchedules > 1 && (
                <button
                  type="button"
                  onClick={onGenerateRandomSchedule}
                  className="inline-flex min-h-12 min-w-12 items-center justify-center rounded-control text-slate-600 transition-colors hover:bg-slate-200/70 hover:text-slate-900 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-700/80 dark:hover:text-white"
                  title="Generate random schedule"
                  aria-label="Generate random schedule"
                  disabled={(totalSchedulesGenerated ?? 0) === 0}
                >
                  <FunctionalIcon name="shuffle" className="text-xl" />
                </button>
              )}
            </div>
          )}

          {/* Credits and Save */}
          <div className="flex items-center space-x-3">
            {(() => {
              const totalCredits = schedule.reduce((sum, course) => {
                const credits = typeof course.credits === 'string' ? parseFloat(course.credits) : course.credits;
                return sum + (credits || 0);
              }, 0);
              const isCreditWarning = showCreditLoadStatus && (totalCredits > 20 || totalCredits < 12);

              return (
                <div className="flex items-center space-x-2">
                  {regenIndicatorVisible && (
                    <span className="inline-flex items-center text-[11px] font-semibold text-blue-700 dark:text-blue-300">
                      <ArrowPathIcon className="h-3.5 w-3.5 mr-1 animate-spin" />
                      Updating
                    </span>
                  )}
                  <div className={`text-sm font-medium px-2 py-1 rounded-control ${isCreditWarning
                    ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-700'
                    : 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700'
                    }`}>
                    <span>{totalCredits} Credits</span>
                  </div>
                </div>
              );
            })()}


          </div>
        </div>

        {contentOverride ? <div className="border-t border-[var(--ss-border)] pt-3">{contentOverride}</div> : (
        <>{/* Mobile Course List by Day */}
        <div className="space-y-4">
          {DAYS.map(day => {
            const dayCourses = (showOwnSchedule ? schedule : []).filter(course =>
              course.meetingTimes?.some(mt => mt.day === day)
            );

            const dayFriendCourses = (overlayFriendSchedules ?? []).flatMap(friend => friend.courses.flatMap(course => course.meetingTimes.filter(meeting => meeting.day === day).map(meeting => ({ friend, course, meeting }))));
            if (dayCourses.length === 0 && dayFriendCourses.length === 0) return null;

            return (
              <div key={day} className="bg-white dark:bg-slate-800 rounded-control border border-slate-300 dark:border-slate-700 p-3">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-3">{day}</h3>
                <div className="space-y-3">
                  {dayCourses.map((course, courseIndex) => {
                    const meetingTimes = course.meetingTimes.filter(mt => mt.day === day);
                    const campusStyleBase = getCampusStyle(getPrimaryCampusValue(course));
                    const campusDisplay = formatCampusDisplay(course);
                    const customActivity = isCustomActivity(course);

                    return meetingTimes.map((meetingTime, meetingIndex) => {
                      const startTime = formatTime(meetingTime.startMinute);
                      const endTime = formatTime(meetingTime.endMinute);
                      const campusStyle = customActivity ? 'border-l-slate-900 bg-gradient-to-r from-slate-700 to-slate-600' : campusStyleBase;

                      return (
                        <div
                          key={`${course.id}-${meetingIndex}`}
                          className={`rounded-control p-3 border-l-4 ${campusStyle}`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-bold text-white text-sm">
                              {isRecitationMeeting(meetingTime) && <abbr title="Recitation" className="mr-1 rounded bg-slate-600 px-1 py-0.5 text-white no-underline">R</abbr>}{course.name}
                            </h4>
                            <span className="text-xs text-white/80">
                              {formatBaseIdDisplay(course.baseId)}
                            </span>
                          </div>

                          <div className="space-y-1 text-xs text-white/90">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium">{startTime} - {endTime}</span>
                              <span className="text-white/60">•</span>
                              <span>{course.location}</span>
                            </div>

                            <div className="flex items-center space-x-2">
                              <span>{course.instructors.join(', ')}</span>
                              <span className="text-white/60">•</span>
                              <span>{customActivity ? 'Blocked time' : `${course.credits} credits`}</span>
                            </div>

                            <div className="flex items-center space-x-2">
                              <span className="capitalize">{campusDisplay}</span>
                              <span className="text-white/60">•</span>
                              <span>{customActivity ? 'Custom activity' : `Section ${course.section}`}</span>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })}
                  {dayFriendCourses.map(({ friend, course, meeting }, index) => <div key={`${friend.friendName}-${course.id}-${index}`} className="rounded-control border-l-4 bg-[var(--ss-surface-subtle)] p-3" style={{ borderColor: friend.color ?? 'var(--ss-info)' }}><p className="text-xs font-semibold text-[var(--ss-text-muted)]">{friend.friendName}</p><p className="mt-1 text-sm font-semibold text-[var(--ss-text)]">{course.name}</p><p className="mt-1 text-xs text-[var(--ss-text-muted)]">{formatTime(meeting.startMinute)}–{formatTime(meeting.endMinute)}</p></div>)}
                </div>
              </div>
            );
          })}
        </div></>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className="ss-scheduling-weekly min-h-full w-full overflow-hidden p-3 sm:p-4">
      {/* Header Section */}
      <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="mb-3 sm:mb-0">
          <div className="mb-2 flex items-center justify-center gap-2 sm:justify-start">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white sm:text-xl">Weekly Schedule</h2>
            {!isSelectedSchedule && onHomeSchedule ? (
              <button
                onClick={onHomeSchedule}
                className="flex min-h-10 min-w-10 items-center justify-center rounded-control bg-blue-200 text-blue-900 transition-colors hover:bg-blue-300 dark:bg-blue-700 dark:text-white dark:hover:bg-blue-600"
                title="Return to selected schedule"
                aria-label="Return to selected schedule"
              >
                <HomeIcon className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          {schedule.length === 0 && (selectedCourseCount ?? 0) === 0 && <p className="text-xs text-[var(--ss-text-muted)]">Add classes to see a schedule</p>}
          {totalSchedulesGenerated === 0 && !isSelectedSchedule && (
            <div className="flex items-center gap-3 mb-2">
              <div className="text-slate-600 dark:text-slate-400 text-sm">
                0 Schedules found with configuration
              </div>
              {noScheduleReasons && noScheduleReasons.length > 0 && onOpenNoScheduleModal && (
                <button
                  onClick={onOpenNoScheduleModal}
                  className="px-3 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-control transition-colors"
                  title="View detailed reasons why schedules couldn't be generated"
                >
                  Why?
                </button>
              )}
            </div>
          )}
          {(totalSchedulesGenerated && totalSchedulesGenerated > 0) && (
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={onPrevSchedule}
                className="flex min-h-10 min-w-10 items-center justify-center rounded-control bg-slate-200 text-slate-900 transition-colors hover:bg-slate-300 disabled:opacity-50 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600"
                disabled={(totalSchedulesGenerated ?? 0) === 0}
                aria-label="Previous schedule"
                title="Previous schedule (Left arrow)"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <div className="flex flex-col text-xs leading-tight text-slate-600 dark:text-slate-400 sm:text-sm">
                <span>{currentScheduleIndex === -1 ? 'Selected Schedule' : `Schedule ${currentScheduleIndex + 1} of ${totalSchedules}`}</span>
                {totalSchedulesGenerated && totalSchedulesGenerated !== totalSchedules && currentScheduleIndex !== -1 ? (
                  <span className="mt-0.5 text-[10px] text-slate-500 dark:text-slate-500">{totalSchedulesGenerated} generated</span>
                ) : null}
              </div>
              <button
                onClick={onNextSchedule}
                className="flex min-h-10 min-w-10 items-center justify-center rounded-control bg-slate-200 text-slate-900 transition-colors hover:bg-slate-300 disabled:opacity-50 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600"
                disabled={(totalSchedulesGenerated ?? 0) === 0 || (!isSelectedSchedule && currentScheduleIndex === totalSchedules - 1)}
                aria-label="Next schedule"
                title="Next schedule (Right arrow)"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
              {onGenerateRandomSchedule && totalSchedules > 1 && (
                <button
                  type="button"
                  onClick={onGenerateRandomSchedule}
                  className="inline-flex min-h-12 min-w-12 items-center justify-center rounded-control text-slate-600 transition-colors hover:bg-slate-200/70 hover:text-slate-900 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-700/80 dark:hover:text-white"
                  title="Generate random schedule"
                  aria-label="Generate random schedule"
                  disabled={(totalSchedulesGenerated ?? 0) === 0}
                >
                  <FunctionalIcon name="shuffle" className="text-xl" />
                </button>
              )}
            </div>
          )}

        </div>
        <div className="flex flex-col items-center gap-3 sm:items-end">
          {workspaceTabs}
          <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-4">
          {headerActions}
          {/* Credit Counter */}
          {(() => {
            const totalCredits = schedule.reduce((sum, course) => {
              const credits = typeof course.credits === 'string' ? parseFloat(course.credits) : course.credits;
              return sum + (credits || 0);
            }, 0);
            const isCreditWarning = showCreditLoadStatus && (totalCredits > 20 || totalCredits < 12);

            return (
              <div className="flex items-center space-x-3">
                {regenIndicatorVisible && (
                  <span className="inline-flex items-center text-xs font-semibold text-blue-700 dark:text-blue-300">
                    <ArrowPathIcon className="h-3.5 w-3.5 mr-1 animate-spin" />
                    Updating
                  </span>
                )}
                <div className={`text-sm sm:text-base font-medium px-2.5 py-1.5 rounded-control inline-flex items-center ${isCreditWarning
                  ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-700'
                  : 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700'
                  }`}>
                  <span>{totalCredits} Credits</span>
                  {isCreditWarning && (
                    <span className="text-xs ml-2">
                      {totalCredits > 20 ? '(Too many)' : '(Too few)'}
                    </span>
                  )}
                </div>
              </div>
            );
          })()}


          </div>
          {/* Google Calendar Export Button - Show if export function is provided and schedule has courses */}
          {/* {onExportToGoogleCalendar && schedule.length > 0 && (
            <button 
              onClick={async () => {
                if (isExporting) return; // Prevent multiple clicks
                setIsExporting(true);
                try {
                  const result = await onExportToGoogleCalendar(schedule);
                  setToast({
                    message: result.message,
                    type: result.success ? 'success' : 'error',
                    isVisible: true
                  });
                } catch (error) {
                  setToast({
                    message: 'Failed to export to Google Calendar',
                    type: 'error',
                    isVisible: true
                  });
                } finally {
                  setIsExporting(false);
                }
              }}
              disabled={isExporting}
              className="px-4 py-2 sm:px-6 sm:py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white font-medium rounded-control transition-colors text-sm sm:text-base flex items-center space-x-2"
            >
              {isExporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <span><FunctionalIcon name="calendar" /></span>
                  <span>Export to Google Calendar</span>
                </>
              )}
            </button>
          )} */}

          {/* Removed score display; credits badge above serves as the primary badge */}
        </div>
      </div>

      {contentOverride ? <div className="border-t border-[var(--ss-border)] pt-4">{contentOverride}</div> : (
      <>{/* Schedule Grid Container */}
      <div className="ss-scheduling-grid rounded-card p-1.5 sm:p-3">
        {/* Horizontal scroll container for small screens */}
        <div className="overflow-x-auto sm:overflow-x-visible">
          {/* Schedule header - days of the week */}
          <div className="grid min-w-[700px] grid-cols-6 gap-1 sm:gap-2 mb-2">
            <div className="text-center">
              <div className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm font-medium h-12 flex items-center justify-center gap-2">
                <span>Time</span>
                {typeof currentScheduleScore === 'number' && (
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold border
                      ${currentScheduleScore >= 80
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700'
                        : currentScheduleScore >= 60
                          ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700'}`}
                    title="Schedule score"
                  >
                    {currentScheduleScore}%
                  </span>
                )}
              </div>
            </div>
            {DAYS.map(day => (
              <div key={day} className="text-center">
                <div className="bg-slate-300 dark:bg-slate-700 text-slate-900 dark:text-white font-bold py-1.5 sm:py-2.5 rounded-control">
                  <div className="text-xs sm:text-base">{isSmallScreen ? day.substring(0, 1) : day}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Schedule grid */}
          <div className="relative grid min-w-[700px] grid-cols-6 gap-1 sm:gap-2" style={{ height: `${totalHours * hourHeight}px` }}>
            {/* Time markers column */}
            <div className="relative">
              {HOURS.map((hour) => (
                <div
                  key={hour}
                  className="absolute w-full text-xs sm:text-sm text-slate-600 dark:text-slate-400 font-medium flex items-center justify-center border-t border-slate-300 dark:border-slate-700"
                  style={{
                    top: `${(hour - earliestHour) * hourHeight}px`,
                    height: `${hourHeight}px`
                  }}
                >
                  <span className="bg-slate-200 dark:bg-slate-800 px-1 sm:px-2 py-0.5 rounded">
                    {formatTime(hour * 60)}
                  </span>
                </div>
              ))}
            </div>

            {/* Days columns */}
            {DAYS.map(day => (
              <div
                key={day}
                className="relative cursor-crosshair select-none bg-white dark:bg-slate-800 rounded-control border border-slate-300 dark:border-slate-700"
                onMouseDown={(event) => handleTimeBlockMouseDown(event, day)}
                onMouseMove={(event) => handleTimeBlockMouseMove(event, day)}
                onMouseUp={(event) => handleTimeBlockMouseUp(event, day)}
              >
                {/* Background hour lines */}
                {HOURS.map((hour) => (
                  <div
                    key={hour}
                    className="absolute w-full border-t border-slate-200 dark:border-slate-700/50"
                    style={{
                      top: `${(hour - earliestHour) * hourHeight}px`,
                      height: '1px',
                      zIndex: 2,
                    }}
                  />
                ))}

                {showAvailabilityOverlay && renderAvailabilityTint(day)}

                {(() => {
                  const selection = dragSelection?.day === day
                    ? dragSelection
                    : selectedTimeBlock?.day === day
                    ? selectedTimeBlock
                    : null;
                  if (!selection) return null;
                  const topPosition = ((selection.startMinute / 60) - earliestHour) * hourHeight;
                  const height = Math.max(14, ((selection.endMinute - selection.startMinute) / 60) * hourHeight);
                  const canFitInlineActions = !dragSelection && height >= 168;
                  return (
                    <div
                      className={`absolute left-1 right-1 rounded-control border-2 border-red-500 bg-red-500/20 shadow-inner ${dragSelection ? 'pointer-events-none' : ''}`}
                      onMouseDown={(event) => event.stopPropagation()}
                      onMouseMove={(event) => event.stopPropagation()}
                      onMouseUp={(event) => event.stopPropagation()}
                      style={{
                        top: `${topPosition + 2}px`,
                        height: `${height - 4}px`,
                        zIndex: !dragSelection && selectedTimeBlock?.day === day ? 35 : 15,
                      }}
                    >
                      <div className="px-2 py-1 text-[10px] font-semibold text-red-800 dark:text-red-100">
                        {formatTime(selection.startMinute)} - {formatTime(selection.endMinute)}
                      </div>
                      {!dragSelection && selectedTimeBlock?.day === day && (
                        <div
                          className={`pointer-events-auto absolute left-1 z-[70] w-[min(14rem,calc(100%-0.5rem))] rounded-card p-2.5 ${popupSurfaceClassName} ${
                            canFitInlineActions ? 'bottom-1' : 'top-full mt-2'
                          }`}
                        >
                          <div className="space-y-1.5">
                            <button
                              type="button"
                              onClick={() => {
                                onFindClassesInTimeBlock?.(selectedTimeBlock);
                                setSelectedTimeBlock(null);
                              }}
                              className={`flex w-full items-center gap-2 rounded-control px-2.5 py-2 text-left text-[11px] font-semibold transition-colors ${popupHoverClassName}`}
                            >
                              <MagnifyingGlassIcon className="h-6 w-6 text-blue-300" />
                              Classes
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                onTimeBlockSelect?.(selectedTimeBlock);
                                setSelectedTimeBlock(null);
                              }}
                              className={`flex w-full items-center gap-2 rounded-control px-2.5 py-2 text-left text-[11px] font-semibold transition-colors ${popupHoverClassName}`}
                            >
                              <PlusIcon className="h-6 w-6 text-red-300" />
                              Activity
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                onMakeTimeBlockAvailable?.(selectedTimeBlock);
                                setSelectedTimeBlock(null);
                              }}
                              className={`flex w-full items-center gap-2 rounded-control px-2.5 py-2 text-left text-[11px] font-semibold transition-colors ${popupHoverClassName}`}
                            >
                              <CheckCircleIcon className="h-6 w-6 text-green-300" />
                              Available
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Hover/focus previews compare sections without changing the schedule. */}
                {alternativeSections.flatMap(section => section.meetingTimes.filter(meeting =>
                  meeting.day === day && Number.isFinite(meeting.startMinute) && Number.isFinite(meeting.endMinute)
                  && meeting.endMinute > meeting.startMinute
                  && meeting.startMinute >= earliestHour * 60
                  && meeting.endMinute <= (earliestHour + totalHours) * 60
                ).map((meeting, index) => (
                  <div
                    key={`alternative-${section.id}-${day}-${index}`}
                    role="img"
                    aria-label={`Other section ${section.section}, ${day}, ${formatTime(meeting.startMinute)} to ${formatTime(meeting.endMinute)}${showSectionAvailability ? `, ${isSectionOpen(section.id) ? 'open' : 'closed'}` : ''}`}
                    className="pointer-events-none absolute left-1 right-1 z-30 hidden overflow-hidden rounded-control border-2 border-dashed border-[var(--ss-focus)] bg-[var(--ss-surface-subtle)] px-1.5 py-1 text-xs text-[var(--ss-text-muted)] md:block"
                    style={{
                      top: `${(meeting.startMinute / 60 - earliestHour) * hourHeight + 2}px`,
                      height: `${Math.max(0, (meeting.endMinute - meeting.startMinute) / 60 * hourHeight - 4)}px`,
                    }}
                  >
                    <p className="truncate font-semibold">Section {section.section}{isRecitationMeeting(meeting) ? ' · R' : ''}</p>
                    {showSectionAvailability && <p>{isSectionOpen(section.id) ? 'Open' : 'Closed'}</p>}
                  </div>
                )))}

                {/* Visibility changes the comparison canvas, never the saved schedule. */}
                {(showOwnSchedule ? schedule : []).filter(course =>
                  course.meetingTimes?.some(mt => mt.day === day)
                ).flatMap((course, courseIndex) => {
                  // Get all meeting times for this course on this day
                  const meetingTimes = course.meetingTimes.filter(mt => mt.day === day);

                  return meetingTimes.map((meetingTime, meetingIndex) => {
                    const startMinute = meetingTime.startMinute;
                    const endMinute = meetingTime.endMinute;

                    const startHour = startMinute / 60;
                    const duration = (endMinute - startMinute) / 60; // in hours

                    const topPosition = (startHour - earliestHour) * hourHeight;
                    const height = duration * hourHeight;
                    const showLocation = height >= 90;
                    const showInstructor = height >= 110;
                    const hasMultipleCampuses = (course.campuses || []).length > 1;
                    const showCampus = height >= 130 || (hasMultipleCampuses && height >= 90);
                    const meetingCampusValue = getMeetingCampusValue(course, meetingTime);
                    const campusDisplay = meetingTime.campus?.trim()?.length
                      ? meetingTime.campus
                      : formatCampusDisplay(course);
                    const customActivity = isCustomActivity(course);
                    const changeState = courseChangeStates[course.id];

                    return (
                      <div
                        key={`${course.id}-${courseIndex}-${meetingIndex}`}
                        role="button"
                        tabIndex={0}
                        aria-describedby={hoverPreview?.course.id === course.id ? 'schedule-grid-course-preview' : undefined}
                        className={`absolute left-1 right-1 ${height < 70 ? 'p-1.5' : 'p-1.5 sm:p-2'} rounded-control text-white shadow-e2 hover:shadow-e3 transition-[box-shadow,opacity,transform] duration-200 motion-reduce:transition-none cursor-pointer border-l-4 ${customActivity ? 'border-l-slate-900 bg-gradient-to-r from-slate-700 to-slate-600' : getCampusStyle(meetingCampusValue)} ${selectedCourseId === course.id ? 'ring-4 ring-sky-200 ring-offset-2 ring-offset-white dark:ring-sky-500/80 dark:ring-offset-gray-900 scale-[1.01]' : ''} ${isCourseInConflict(course.id) ? 'ring-2 ring-red-500 ring-opacity-75 animate-pulse' : ''} ${changeState === 'added' ? 'border-2 border-dashed border-emerald-200 ring-2 ring-emerald-500/70' : ''} ${changeState === 'removed' ? 'border-2 border-dashed border-red-200 opacity-50 ring-2 ring-red-500/70' : ''
                          }`}
                        style={{
                          top: `${topPosition + 2}px`,
                          height: `${height - 4}px`,
                          overflow: 'visible',
                          zIndex: hoverPreview?.course.id === course.id ? 40 : 20,
                        }}
                        onMouseEnter={(e) => {
                          openHoverPreview(e.currentTarget, course);
                        }}
                        onMouseLeave={() => {
                          closeHoverPreview();
                        }}
                        onFocus={(event) => {
                          const anchor = event.currentTarget;
                          // Native focus may scroll the timetable before the preview is positioned.
                          requestAnimationFrame(() => {
                            if (document.activeElement === anchor) openHoverPreview(anchor, course);
                          });
                        }}
                        onBlur={closeHoverPreview}
                        onKeyDown={(e) => handleCourseCardKeyDown(e, course)}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => handleCourseCardClick(e, course)}
                        onContextMenu={(e) => handleContextMenu(e, course)}
                      >
                        <span className="sr-only">{meetingTime.day}, {formatTime(startMinute)} to {formatTime(endMinute)}. </span>
                        <div className="h-full flex flex-col justify-between">
                          <div>
                            {changeState ? (
                              <div className="mb-1 w-fit rounded bg-white px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-slate-900">
                                {changeState === 'added' ? 'Proposed add' : 'Remove'}
                              </div>
                            ) : null}
                            <div className="text-[11px] sm:text-xs font-bold leading-tight mb-1 truncate">
                              {isRecitationMeeting(meetingTime) && <abbr title="Recitation" className="mr-1 rounded bg-slate-600 px-1 py-0.5 text-white no-underline">R</abbr>}{course.name}
                            </div>
                            <div className={`text-xs opacity-90 ${height < 70 ? 'hidden' : ''}`}>
                              {customActivity ? 'Custom activity' : `${formatBaseIdDisplay(course.baseId)} • ${course.section}`}
                            </div>
                            <div className="mt-1 flex items-center gap-2 justify-start flex-wrap">
                              {(customActivity || showSectionAvailability) && <span className={`px-1 py-0.5 rounded text-[10px] leading-tight font-medium ${customActivity
                                ? 'bg-white/20 text-white'
                                : isSectionOpen(course.id)
                                  ? 'bg-green-700 text-white'
                                  : 'bg-red-600 text-white'
                              }`}>
                                {customActivity ? 'BLOCKED' : isSectionOpen(course.id) ? 'OPEN' : 'CLOSED'}
                              </span>}
                              {/* Friend avatars to the right of the status pill */}
                              {friendPresenceBySection && friendPresenceBySection[course.id] && friendPresenceBySection[course.id].length > 0 && (
                                <div className="flex items-center -space-x-2 justify-start">
                                  {friendPresenceBySection[course.id].slice(0, 4).map((f, idx) => (
                                    <span key={`${f.userId}-${idx}`} className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white ring-2 ring-white overflow-hidden shadow-e1">
                                      <Image
                                        width={24}
                                        height={24}
                                        sizes="24px"
                                        unoptimized={Boolean(f.avatarUrl && !/^https:\/\/(?:lh3\.googleusercontent\.com\/a\/|akashdubey\.me\/images\/)/.test(f.avatarUrl))}
                                        referrerPolicy="no-referrer"
                                        src={f.avatarUrl || '/favicon-32x32.png'}
                                        alt={f.displayName || 'Friend'}
                                        title={f.displayName || 'Friend'}
                                        className="h-full w-full object-cover"
                                      />
                                    </span>
                                  ))}
                                  {friendPresenceBySection[course.id].length > 4 && (
                                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-800 text-white text-[10px] font-semibold ring-2 ring-white">+{friendPresenceBySection[course.id].length - 4}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {(showLocation || showInstructor || showCampus) && (
                            <div className="text-xs opacity-80">
                              {showLocation && (
                                <div className="truncate mb-1">
                                  <FunctionalIcon name="location" /> {meetingTime.location || course.location || 'TBA'}
                                </div>
                              )}
                              {showInstructor && course.instructors && course.instructors.length > 0 && course.instructors[0] !== 'TBA' && (
                                <div className="truncate">
                                  <FunctionalIcon name="instructor" /> {typeof course.instructors[0] === 'string' ? course.instructors[0] : course.instructors[0].name}
                                </div>
                              )}
                              {showCampus && (
                                <div className="text-xs opacity-60 mt-1">
                                  <FunctionalIcon name="campus" /> {campusDisplay}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })}

                {/* Overlay Friend Schedules */}
                {overlayFriendSchedules && overlayFriendSchedules.flatMap((friendObj, friendIdx) => {
                  return friendObj.courses.filter(course =>
                    course.meetingTimes?.some(mt => mt.day === day)
                  ).flatMap((course, courseIndex) => {
                    const meetingTimes = course.meetingTimes.filter(mt => mt.day === day);

                    return meetingTimes.map((meetingTime, meetingIndex) => {
                      const startMinute = meetingTime.startMinute;
                      const endMinute = meetingTime.endMinute;
                      const startHour = startMinute / 60;
                      const duration = (endMinute - startMinute) / 60;

                      const topPosition = (startHour - earliestHour) * hourHeight;
                      const height = duration * hourHeight;

                      const isSharedSection = schedule.some(c => c.id === course.id);
                      const isSharedClassWithDiffSection = !isSharedSection && schedule.some(c => c.baseId === course.baseId);

                      // Add distinctive styling for friend overlays
                      return (
                        <div
                          key={`friend-${friendIdx}-${course.id}-${courseIndex}-${meetingIndex}`}
                          className={`absolute left-0 right-0 p-1.5 sm:p-2 rounded-control border-2 shadow-none`}
                          style={{
                            top: `${topPosition + 2}px`,
                            height: `${height - 4}px`,
                            zIndex: 25,
                            opacity: isSharedSection ? 0.85 : 0.75,
                            borderStyle: isSharedSection ? 'solid' : 'dashed',
                            pointerEvents: 'none',
                            backgroundColor: friendObj.color ?? friendOverlayColor(friendIdx),
                            borderColor: friendObj.color ?? friendOverlayColor(friendIdx)
                          }}
                        >
                          <div className="h-full flex flex-col justify-start">
                            <div className="text-[10px] font-bold leading-tight truncate text-white drop-shadow-md">
                              {course.name}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              <div className="text-[8px] bg-white/70 text-black rounded px-1 py-0.5 inline-block w-fit font-bold shadow-e1">
                                {isSharedSection ? `You & ${friendObj.friendName}` : friendObj.friendName}
                              </div>
                              {isSharedClassWithDiffSection && (
                                <div className="text-[8px] bg-yellow-300 text-yellow-900 rounded px-1 py-0.5 inline-block w-fit font-bold shadow-e1">
                                  Same Class
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  });
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Asynchronous/Online courses section - moved outside the grid container */}
      {schedule.filter(course =>
        !course.meetingTimes ||
        course.meetingTimes.length === 0 ||
        course.meetingTimes.every(mt => !mt.day || mt.day === 'TBA') ||
        formatCampusDisplay(course).toLowerCase().includes('online') ||
        formatCampusDisplay(course).toLowerCase().includes('remote')
      ).length > 0 && (
          <div className="mt-6 p-3 sm:p-4">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-3 flex items-center">
              <span className="mr-2"><FunctionalIcon name="display" /></span>
              Online & Asynchronous Courses
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {schedule.filter(course =>
                !course.meetingTimes ||
                course.meetingTimes.length === 0 ||
                course.meetingTimes.every(mt => !mt.day || mt.day === 'TBA') ||
                formatCampusDisplay(course).toLowerCase().includes('online') ||
                formatCampusDisplay(course).toLowerCase().includes('remote')
              ).map((course, index) => {
                const campusDescriptor = formatCampusDisplay(course);
                const campusDescriptorLower = campusDescriptor.toLowerCase();
                const changeState = courseChangeStates[course.id];

                return (
                  <div
                    key={`async-${course.id}-${index}`}
                    className={`p-3 rounded-control shadow-e2 border-l-4 text-white hover:shadow-e3 transition-[box-shadow,opacity,transform] duration-200 motion-reduce:transition-none cursor-pointer ${getCampusStyle(getPrimaryCampusValue(course))} ${selectedCourseId === course.id ? 'ring-4 ring-sky-200 ring-offset-2 ring-offset-white dark:ring-sky-500/80 dark:ring-offset-gray-900 scale-[1.01]' : ''} ${changeState === 'added' ? 'border-2 border-dashed border-emerald-200 ring-2 ring-emerald-500/70' : ''} ${changeState === 'removed' ? 'border-2 border-dashed border-red-200 opacity-50 ring-2 ring-red-500/70' : ''}`}
                    onClick={(e) => handleCourseCardClick(e, course)}
                    onContextMenu={(e) => handleCourseCardRightClick(e, course)}
                  >
                    {changeState ? <div className="mb-2 w-fit rounded bg-white px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-slate-900">{changeState === 'added' ? 'Proposed add' : 'Remove'}</div> : null}
                    <div className="font-bold text-base mb-2">{course.name}</div>
                    <div className="text-sm opacity-90">
                      {formatBaseIdDisplay(course.baseId)} • {course.section}
                    </div>
                    <div className="text-xs uppercase tracking-wide opacity-80">
                      Class Index {formatClassIndex(course.id)}
                    </div>
                    {showSectionAvailability && <div className="mt-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${isSectionOpen(course.id)
                        ? 'bg-green-700 text-white'
                        : 'bg-red-600 text-white'
                        }`}>
                        {isSectionOpen(course.id) ? 'OPEN' : 'CLOSED'}
                      </span>
                    </div>}

                    <div className="space-y-2 text-sm">
                      {course.instructors && course.instructors.length > 0 && course.instructors[0] !== 'TBA' && (
                        <div className="opacity-90">
                          {course.instructors.map(instructor =>
                            typeof instructor === 'string' ? instructor : instructor.name
                          ).join(', ')}
                        </div>
                      )}

                      <div className="opacity-90">
                        {campusDescriptor}
                      </div>

                      <div className="opacity-90">
                        {campusDescriptorLower.includes('online') ? 'Online Course' :
                          campusDescriptorLower.includes('remote') ? 'Remote Learning' :
                            'Asynchronous'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      {/* Campus Color Key */}
      <div className="mt-6 p-3 bg-slate-100 dark:bg-slate-700 rounded-control">
        <h4 className="text-base font-bold text-slate-900 dark:text-white mb-3 flex items-center">
          <span className="mr-2"><FunctionalIcon name="colors" /></span>
          Campus Color Key
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {CAMPUS_COLOR_KEY_ENTRIES.map((campus) => (
            <div key={campus} className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded border ${CAMPUS_COLORS[campus]}`}></div>
              <span className="text-sm text-slate-700 dark:text-slate-300">{campus}</span>
            </div>
          ))}
        </div>
      </div>

      {showDetailedCourseInformation && (
      <div className="mt-6 rounded-control bg-slate-50 p-3 sm:p-4 dark:bg-slate-800">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              Detailed Course Information
            </h3>
            {schedule.length > 0 && (
              <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                Class indexes: {schedule
                  .filter(course => !isCustomActivity(course))
                  .map((course) => formatClassIndex(course.id))
                  .filter((value, index, self) => self.indexOf(value) === index)
                  .join(', ') || 'None'}
              </p>
            )}
          </div>
          <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
            Expand cards for ratings, meetings, and notes.
          </span>
        </div>
                <div className="grid grid-cols-1 gap-3 overflow-visible md:grid-cols-2 xl:grid-cols-4">
          {schedule.map((course, index) => {
            const customActivity = isCustomActivity(course);
            const instructorNames = course.instructors.map(instructor =>
              typeof instructor === 'string' ? instructor : instructor.name
            ).join(', ');
            const professorData = customActivity ? null : findProfessorData(instructorNames);

            return (
              <details
                key={`details-${course.id}-${index}`}
                className={`group relative overflow-visible rounded-control border-l-4 text-white shadow-e2 transition-[box-shadow,opacity,transform] duration-200 motion-reduce:transition-none hover:z-30 hover:shadow-e3 open:z-30 open:shadow-e3 ${customActivity ? 'border-l-slate-500 bg-gradient-to-br from-slate-700 to-slate-600' : getCampusStyle(getPrimaryCampusValue(course))}`}
                onContextMenu={(e) => handleCourseCardRightClick(e, course)}
              >
                <summary className="cursor-pointer list-none p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h4 className="truncate text-sm font-bold uppercase">{course.name}</h4>
                      <p className="mt-1 text-xs opacity-90">
                        {customActivity ? 'Custom activity' : `${formatBaseIdDisplay(course.baseId)} • Section ${course.section}`}
                      </p>
                      {!customActivity && instructorNames && instructorNames !== 'TBA' && (
                        <p className="mt-1 truncate text-[11px] font-medium opacity-85">
                          {instructorNames}
                        </p>
                      )}
                    </div>
                    {(customActivity || showSectionAvailability) && <span className="shrink-0 rounded bg-white/20 px-2 py-1 text-[10px] font-semibold uppercase">
                      {customActivity ? 'Blocked' : (isSectionOpen(course.id) ? 'Open' : 'Closed')}
                    </span>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] opacity-90">
                    {!customActivity && hasRecitationMeeting(course.meetingTimes) && (
                      <span className="rounded bg-white/20 px-1.5 py-0.5 font-semibold">Recitation</span>
                    )}
                    {!customActivity && (
                      <span className="rounded bg-white/15 px-1.5 py-0.5">Index {formatClassIndex(course.id)}</span>
                    )}
                    {course.credits && !customActivity && (
                      <span className="rounded bg-white/15 px-1.5 py-0.5">{course.credits} cr</span>
                    )}
                    <span className="rounded bg-white/15 px-1.5 py-0.5">{formatCampusDisplay(course)}</span>
                  </div>
                </summary>

                <div className={`pointer-events-none absolute left-2 right-2 top-[calc(100%-0.25rem)] z-50 hidden rounded-control p-3 text-xs shadow-e3 group-hover:block group-focus-within:block ${popupSurfaceClassName}`}>
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate font-bold uppercase">{course.name}</div>
                      <div className={`mt-0.5 ${popupMutedClassName}`}>
                        {customActivity ? 'Custom activity' : `${formatBaseIdDisplay(course.baseId)} • Section ${course.section}`}
                      </div>
                    </div>
                    <span className={`shrink-0 rounded px-2 py-0.5 text-[10px] uppercase ${popupSubtleClassName}`}>
                      {customActivity ? 'Blocked' : formatClassIndex(course.id)}
                    </span>
                  </div>
                  {!customActivity && instructorNames && (
                    <div className="mb-2">
                      <span className={popupMutedClassName}>Professor </span>
                      <span className="font-semibold">{instructorNames}</span>
                    </div>
                  )}
                  {!customActivity && professorData && (
                    <div className={`mb-2 grid grid-cols-2 gap-2 border-y py-2 ${theme === 'light' ? 'border-slate-200' : 'border-white/10'}`}>
                      <div>
                        <div className={popupMutedClassName}>Rating</div>
                        <div className="font-semibold">{professorData.avgRating.toFixed(1)} / 5.0</div>
                      </div>
                      <div>
                        <div className={popupMutedClassName}>Difficulty</div>
                        <div className="font-semibold">{professorData.avgDifficulty.toFixed(1)} / 5.0</div>
                      </div>
                    </div>
                  )}
                  {course.meetingTimes && course.meetingTimes.length > 0 && (
                    <div className="space-y-1">
                      {course.meetingTimes.slice(0, 3).map((meeting, idx) => (
                        <div key={idx} className={`flex justify-between gap-2 rounded px-2 py-1 ${popupSubtleClassName}`}>
                          <span className="truncate">{meeting.day.substring(0, 3)}. {meeting.location || formatCampusDisplay(course)}</span>
                          <span className={`shrink-0 ${popupMutedClassName}`}>{formatTime(meeting.startMinute).toLowerCase()} - {formatTime(meeting.endMinute).toLowerCase()}</span>
                        </div>
                      ))}
                      {course.meetingTimes.length > 3 && (
                        <div className={popupMutedClassName}>+{course.meetingTimes.length - 3} more meeting{course.meetingTimes.length - 3 === 1 ? '' : 's'}</div>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2 border-t border-white/20 px-3 pb-3 pt-2 text-sm">
                  {friendPresenceBySection && friendPresenceBySection[course.id] && friendPresenceBySection[course.id].length > 0 && (
                    <div className="rounded-control bg-white/20 px-2 py-1 text-xs">
                      <span className="font-semibold">Friends: </span>
                      {friendPresenceBySection[course.id]
                        .map(f => f.displayName || 'Friend')
                        .join(', ')}
                    </div>
                  )}

                  {!customActivity && instructorNames && instructorNames !== 'TBA' && (
                    <div>
                      <div className="mb-2 text-sm opacity-90">{instructorNames}</div>
                      <InstructorSIRSRating
                        instructorName={instructorNames}
                        courseSubject={course.department}
                        courseCode={course.class_number}
                        showCourseSpecific={true}
                        lightText={true}
                      />
                      {professorData && (
                        <div className="mt-2 space-y-1 border-t border-white/20 pt-2 text-xs">
                          <div className="opacity-70">Legacy Ratings</div>
                          <div className="flex items-center gap-1">
                            <span className="opacity-80">Rating</span>
                            <span className={`font-bold ${getRatingColor(professorData.avgRating)}`}>
                              {professorData.avgRating.toFixed(1)}
                            </span>
                            <div className="flex">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <StarIcon
                                  key={star}
                                  className={`h-3 w-3 ${star <= Math.round(professorData.avgRating)
                                    ? 'text-yellow-400'
                                    : 'text-slate-400'
                                    }`}
                                />
                              ))}
                            </div>
                          </div>
                          <div className="opacity-90">Difficulty {professorData.avgDifficulty.toFixed(1)} / 5.0</div>
                          <div className="opacity-90">Would Take Again {professorData.wouldTakeAgainPercent.toFixed(1)}%</div>
                          {professorData.numRatings && (
                            <div className="opacity-90">Total Ratings {professorData.numRatings} reviews</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {course.groupName && (
                    <div className="text-sm opacity-90">{course.groupName}</div>
                  )}

                  {course.meetingTimes && course.meetingTimes.length > 0 && (
                    <div className="space-y-1">
                      {course.meetingTimes.map((meeting, idx) => (
                        <div key={idx} className="rounded bg-white/15 px-2 py-1">
                          <div className="text-xs font-semibold opacity-95">
                            {meeting.day.substring(0, 3)}. {meeting.location || ''}
                          </div>
                          <div className="text-xs opacity-80">
                            {formatTime(meeting.startMinute).toLowerCase()} - {formatTime(meeting.endMinute).toLowerCase()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {course.coreRequirements && course.coreRequirements.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {course.coreRequirements.map((core, idx) => (
                        <span key={idx} className="rounded bg-white/20 px-2 py-1 text-xs opacity-90">
                          {core}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </details>
            );
          })}
        </div>
      </div>
      )}

      {hoverPreview && !isPopupOpen && (
        (() => {
          const names = hoverPreview.course.instructors.map((instructor) =>
            typeof instructor === 'string' ? instructor : instructor.name
          ).join(', ');
          const professorData = isCustomActivity(hoverPreview.course) ? null : findProfessorData(names);
          return (
            <ScheduleCoursePreview
              id="schedule-grid-course-preview"
              course={hoverPreview.course}
              isOpen={isSectionOpen(hoverPreview.course.id)}
              showAvailability={showSectionAvailability}
              professorRating={professorData?.avgRating}
              professorDifficulty={professorData?.avgDifficulty}
              className="pointer-events-none fixed z-40 hidden md:block"
              style={{ left: hoverPreview.left, top: hoverPreview.top }}
            />
          );
        })()
      )}</>
      )}

      {/* Context Menu */}
      <ContextMenu
        isOpen={contextMenu.isOpen}
        x={contextMenu.x}
        y={contextMenu.y}
        course={contextMenu.course}
        onClose={closeContextMenu}
        onAddToSniper={handleAddToSniper}
        onViewInExplore={handleViewInExplore}
        onCopyCourseInfo={handleCopyCourseInfo}
        onShareCourse={handleShareCourse}
      />

      {/* Course Details Popup */}
      <CourseDetailsPopup
        isOpen={isPopupOpen && openedPopupTerm === popupTermToken}
        course={popupCourse}
        x={popupPosition.x}
        y={popupPosition.y}
        onClose={closePopup}
        onAddToSniper={handlePopupAddToSniper}
        onViewInExplore={handlePopupViewInExplore}
        onCopyCourseInfo={handleCopyCourseInfo}
        onShareCourse={handleShareCourse}
      />

      {/* Toast Notifications */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />

      {/* Conflict Indicator */}
      {conflicts.length > 0 && onShowConflicts && (
        <div className="fixed bottom-6 left-6 z-40">
          <button
            onClick={onShowConflicts}
            className="group relative bg-red-600 hover:bg-red-700 text-white p-4 rounded-full shadow-e2 hover:shadow-e3 transition-colors duration-150 motion-reduce:transition-none"
            aria-label={`Resolve ${conflicts.length} class conflict${conflicts.length !== 1 ? 's' : ''}`}
          >
            {/* Conflict icon */}
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>

            {/* Badge showing number of conflicts */}
            <div className="absolute -top-2 -right-2 bg-white text-red-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow-e2">
              {conflicts.length}
            </div>

            {/* Tooltip */}
            <div className="absolute bottom-full left-0 mb-3 px-3 py-2 bg-slate-900 text-white text-sm rounded-control shadow-e2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              {conflicts.length} class conflict{conflicts.length !== 1 ? 's' : ''} detected
              <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
            </div>
          </button>
        </div>
      )}

    </div>
  );
});

export default ScheduleView;
